import { useTranslation } from 'react-i18next'
import { Button, ButtonLink, Card, Skeleton, useCopy } from '@/ds'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { featureEnabled } from '@/config/features'
import { mapsHref } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { accesosService } from '@/services/accesos'
import { SectionError } from '../Estancia/sections/SectionError'
import { AccessBack, EmergencyLine, placeName } from './shared'
import { MapEmbed } from './MapEmbed'

/**
 * 8.4 · Cómo llegar (AccesosDetalle.dc.html `directions`, Bloque 4 §4e). Real: dirección, coordenadas y
 * teléfono (E1). Mock: nota de la dirección, transporte, aparcamiento y fuera de horario (D36).
 * El mapa se embebe con las coordenadas disponibles y el botón abre Google Maps para navegación completa.
 */
export function ComoLlegar() {
  const { t } = useTranslation()
  const stay = useLoadedStay()
  const { copied, copy } = useCopy()
  const [data, reload] = useAsync(`dir:${stay.guestCode}|${stay.phase}`, (signal) => accesosService.getDirections(stay, signal))
  const d = data.kind === 'ready' ? data.value : null
  const showAdvancedDirections = featureEnabled('accesosDireccionesAvanzadas')
  const rich = showAdvancedDirections && d !== null && (d.transport.length > 0 || d.parking !== null || d.afterHoursText !== null)
  const address = d ? [d.street, d.locality].filter(Boolean).join(', ') : ''
  return (
    <main className="page">
      <div className="accesos__head">
        <AccessBack />
        <h1>{t('v2.estancia.comoLlegar.titulo')}</h1>
        {d && <p className="page__lead">{rich ? t('v2.accesos.comoLlegar.lead', { name: placeName(stay) }) : t('v2.accesos.comoLlegar.leadSoloDireccion', { name: placeName(stay) })}</p>}
      </div>
      {data.kind === 'loading' && (
        <div aria-busy="true">
          <Skeleton height={360} />
        </div>
      )}
      {data.kind === 'error' && <SectionError code={data.code} onRetry={reload} />}
      {d && (
        <>
          <MapEmbed lat={d.lat} lng={d.lng} address={address} title={t('v2.accesos.comoLlegar.mapaMarcador', { address })} />
          <Card className="accesos__card">
            <div className="accesos__address">
              <div>
                <p className="accesos__row-title">{d.street}</p>
                {(d.locality || (showAdvancedDirections && d.addressNote)) && <p className="accesos__row-text">{[d.locality, showAdvancedDirections ? d.addressNote : null].filter(Boolean).join(' · ')}</p>}
              </div>
              <Button variant="secondary" fullWidth={false} icon={copied ? 'check' : 'copy'} aria-label={copied ? t('v2.ds.digitalKey.copiado') : t('v2.accesos.comoLlegar.copiarDireccion')} onClick={() => void copy(address)}>
                {copied ? t('v2.ds.digitalKey.copiado') : t('v2.ds.digitalKey.copiar')}
              </Button>
            </div>
            <ButtonLink variant="primary" icon="map-pin" href={mapsHref(d.lat, d.lng, address)} target="_blank" rel="noreferrer">
              {t('v2.accesos.comoLlegar.abrirMaps')}
            </ButtonLink>
          </Card>
          {rich && (
            <Card>
              <ul className="accesos__rows">
                {d.transport.map((tr) => (
                  <li key={tr.mode + tr.title} className="accesos__row">
                    <h2 className="accesos__row-title">{tr.title}</h2>
                    <p className="accesos__row-text">{tr.text}</p>
                  </li>
                ))}
                {d.parking && (
                  <li className="accesos__row">
                    <h2 className="accesos__row-title">{t('v2.accesos.comoLlegar.aparcamiento')}</h2>
                    <p className="accesos__row-text">{d.parking.text}</p>
                  </li>
                )}
                {d.afterHoursText && (
                  <li className="accesos__row">
                    <h2 className="accesos__row-title">{t('v2.accesos.comoLlegar.fueraHorario')}</h2>
                    <p className="accesos__row-text">{d.afterHoursText}</p>
                  </li>
                )}
              </ul>
            </Card>
          )}
          <EmergencyLine phone={d.emergencyPhone} />
        </>
      )}
    </main>
  )
}
